<template lang="">
  <div
    class="quantity-input"
    :class="{
      'quantity-input--multi-upload': isMultiUpload,
      'quantity-input--disabled': disabled,
      'quantity-input--custom-size': isCustomSize
    }"
  >
    <button
      :disabled="disabled || Number(quantityValue) <= minQuantity"
      type="button"
      class="quantity-input__button quantity-input__button--left"
      @click="updateQuantityFromButtons(Number(quantityValue) - 1)"
    >
      <UseSvg class="quantity-input__button-icon" icon="minus" />
    </button>
    <input
      v-model="quantityValue"
      type="number"
      class="quantity-input__input js-quantity-form-input"
      :class="{
        'quantity-input__input--grayed-out': Number(quantityValue) === 0
      }"
      :data-variant-id="variantId"
      :disabled="disabled"
      :min="minQuantity"
      step="1"
      @input="updateQuantity($event.target.value)"
    />
    <button
      :disabled="disabled || Number(quantityValue) >= maxQuantityValue"
      type="button"
      class="quantity-input__button quantity-input__button--right"
      @click="updateQuantityFromButtons(Number(quantityValue) + 1)"
    >
      <UseSvg class="quantity-input__button-icon" icon="plus" />
    </button>
  </div>
</template>
<script>
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import debounce from "lodash.debounce";
import { MAX_QUANTITY_VALUE } from "../common/constants";

export default {
  name: "QuantityInput",
  components: { UseSvg },
  props: {
    quantity: {
      type: Number,
      default: 0
    },
    index: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    },
    minQuantity: {
      type: Number,
      default: 1
    },
    isCustomSize: {
      type: Boolean,
      default: false
    },
    variantId: {
      type: [String, Number],
      default: ""
    }
  },
  emits: ["onChange"],
  data() {
    return {
      quantityValue: Number(this.quantity)
    };
  },
  computed: {
    maxQuantityValue() {
      return MAX_QUANTITY_VALUE;
    }
  },
  watch: {
    quantity: {
      handler(newValue) {
        this.quantityValue = newValue;
      }
    }
  },
  beforeMount() {
    this.updateQuantity = debounce(this.updateQuantity, 500);
  },
  methods: {
    validateQuantity(value) {
      return Math.min(Math.max(this.minQuantity, value), this.maxQuantityValue);
    },
    updateQuantityFromButtons(value) {
      const validValue = this.validateQuantity(value);
      this.quantityValue = validValue;
      this.$emit("onChange", { value: validValue, index: this.index });
    },
    updateQuantity(value) {
      const validValue = this.validateQuantity(value);
      this.quantityValue = validValue;
      this.$emit("onChange", { value: validValue, index: this.index });
    }
  }
};
</script>
<style lang="scss" scoped>
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.quantity-input {
  $this: &;

  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  width: 90px;
  border: 1px solid $crafterpay-gray-modern-800;
  border-radius: 4px;
  text-align: center;
  flex-shrink: 0;
  background-color: $white;

  &--multi-upload {
    #{$this}__input {
      width: 30px;
    }
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
    background-color: $section-grey;
  }

  &--custom-size {
    width: 112px;
    justify-content: space-evenly;
  }

  &__input {
    width: 40px;
    height: 100%;
    border: none;
    outline: none;
    background-color: transparent;
    text-align: center;
    font-weight: 700;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    &:read-only {
      #{$this} {
        opacity: 0.5;
        pointer-events: none;
        border: 1px solid $section-grey-border;
      }
    }

    &:focus {
      outline: none;
      #{$this} {
        border: 2px solid $black;
      }
    }

    &:focus:read-only {
      outline: none;
      #{$this} {
        outline: none;
        border: 1px solid $section-grey-border;
      }
    }

    &--grayed-out {
      opacity: 0.5;
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background-color: transparent;
    height: 100%;
    padding: 0;

    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    &--left {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &--right {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }

  &__button-icon {
    width: 15px;
    height: 15px;
    color: $crafterpay-gray-modern-900;

    @include with-desktop {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
